import { z } from 'zod';
import { useEffect, useMemo } from 'react';
import {
    ScimConfigurationAddParameters,
    ScimConfigurationResponse,
    ScimConfigurationUpdateParameters,
    ScimProvisioningStatus,
    ScimProvisioningType,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import {
    createExperimentalZodFormResolver,
    lt,
    showErrorAlert,
    showSuccessAlert,
    useAppClockService,
    useFormControls,
} from '@mews/framework';

export enum ConfigureScimFieldNames {
    Enabled = 'enabled',
    SecretToken = 'secretToken',
    HasPreviousToken = 'hasPreviousToken',
    ProvisioningProvider = 'provisioningProvider',
}

export interface ConfigureScimFormData {
    enabled: boolean;
    secretToken: string;
    hasPreviousToken: boolean;
    provisioningProvider: ScimProvisioningType;
}

const newScimValidationSchema = z.object({
    [ConfigureScimFieldNames.HasPreviousToken]: z.literal(false),
    [ConfigureScimFieldNames.Enabled]: z.boolean(),
    [ConfigureScimFieldNames.SecretToken]: z.string().min(1),
    [ConfigureScimFieldNames.ProvisioningProvider]: z.nativeEnum(ScimProvisioningType),
});

const updateScimValidationSchema = z.object({
    [ConfigureScimFieldNames.HasPreviousToken]: z.literal(true),
    [ConfigureScimFieldNames.Enabled]: z.boolean(),
    [ConfigureScimFieldNames.SecretToken]: z.string().optional(),
    [ConfigureScimFieldNames.ProvisioningProvider]: z.nativeEnum(ScimProvisioningType),
});

export const scimValidationSchema = z.discriminatedUnion(ConfigureScimFieldNames.HasPreviousToken, [
    updateScimValidationSchema,
    newScimValidationSchema,
]);

export const initialScimValues = {
    [ConfigureScimFieldNames.Enabled]: false,
    [ConfigureScimFieldNames.SecretToken]: '',
    [ConfigureScimFieldNames.HasPreviousToken]: false,
    [ConfigureScimFieldNames.ProvisioningProvider]: ScimProvisioningType.Azure,
};

interface UseConfigureScimFormProps {
    id: string;
    data?: ScimConfigurationResponse;
    onSuccess: () => void;
}

export function useConfigureScimForm({ id, data, onSuccess }: UseConfigureScimFormProps) {
    const appClock = useAppClockService();
    const tokenGenerationDateUtc = appClock.getAppNowUtc().value;
    const defaultValues = useMemo(() => (data?.id ? parseScimConfigToForm(data) : initialScimValues), [data]);

    const commonMutationOptions = {
        invalidateQueries: {
            scimConfiguration: ['get' as const],
        },
        meta: {
            disableDefaultErrorHandler: true,
        },
    };

    const { mutateAsync: updateScim } = useRepositoryMutation('scimConfiguration', 'update', commonMutationOptions);
    const { mutateAsync: createScim } = useRepositoryMutation('scimConfiguration', 'add', commonMutationOptions);

    const {
        validate,
        errors,
        reset: internalReset,
        ...otherMethods
    } = useFormControls<ConfigureScimFormData>({
        id,
        onSubmit: async (values: ConfigureScimFormData, { reset }) => {
            try {
                if (data?.id) {
                    await updateScim(parseFormToScimUpdatePayload(data.id, tokenGenerationDateUtc, values));
                } else {
                    await createScim(parseFormToScimCreatePayload(tokenGenerationDateUtc, values));
                }
                showSuccessAlert({
                    title: lt(
                        values.enabled
                            ? 'UserProvisioningStatusChangeActivate'
                            : 'UserProvisioningStatusChangeDeactivate'
                    ),
                });
                onSuccess();
                reset();
            } catch (error) {
                showErrorAlert({ description: <>{error.message}</> });
            }
        },
        mode: 'onBlur',
        defaultValues,
        resolver: createExperimentalZodFormResolver(scimValidationSchema),
    });

    // Since the form is not being mounted/unmounted, we need to reset the form when the default values change
    useEffect(() => {
        internalReset(defaultValues);
    }, [internalReset, defaultValues]);

    return {
        validate,
        reset: () => internalReset(defaultValues),
        ...otherMethods,
    };
}

function parseFormToScimCreatePayload(
    tokenGenerationDateUtc: string,
    { enabled, secretToken, provisioningProvider }: ConfigureScimFormData
): ScimConfigurationAddParameters {
    return {
        token: secretToken,
        tokenGenerationDateUtc,
        provisioningStatus: enabled ? ScimProvisioningStatus.Active : ScimProvisioningStatus.Disabled,
        provisioningType: provisioningProvider,
    };
}

function parseFormToScimUpdatePayload(
    configurationId: string,
    tokenGenerationDateUtc: string,
    { enabled, secretToken, provisioningProvider }: ConfigureScimFormData
): ScimConfigurationUpdateParameters {
    const hasToken = !!secretToken;

    return {
        ...(hasToken && { token: { value: secretToken }, tokenGenerationDateUtc: { value: tokenGenerationDateUtc } }),
        configurationId,
        // This is not correctly mapped on the BE
        provisioningStatus: {
            value: enabled ? ScimProvisioningStatus.Active : ScimProvisioningStatus.Disabled,
        } as $TSFixMe,
        provisioningProvider: { value: provisioningProvider } as $TSFixMe,
    };
}

export function parseScimConfigToForm(data: ScimConfigurationResponse): ConfigureScimFormData {
    return {
        enabled: data.provisioningStatus === ScimProvisioningStatus.Active,
        secretToken: '',
        hasPreviousToken: data.tokenGenerationDateUtc !== null,
        provisioningProvider: data.provisioningType || ScimProvisioningType.Azure,
    };
}
